import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Satellite, Loader2, Eye, EyeOff, Radar } from "lucide-react";
import { getSatEvents, analyseSat, getSat, getPhotos, startPhotos, formatApiError } from "@/lib/api";
import PhotoStrip from "@/components/PhotoStrip";

const fmt = (t) => (t ? new Date(t).toUTCString().slice(5, 22) + " UTC" : "—");
export const SAT_COLORS = { confirmed: "#34D399", radar_only: "#38BDF8", engine_only: "#FBBF24" };
const LABELS = {
  confirmed: "Radar + engine agree",
  radar_only: "Radar saw water, engine did not",
  engine_only: "Engine predicted, radar saw none",
};

function Counts({ v, onSelect }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-1">
      {Object.keys(LABELS).map((k) => (
        <div key={k}>
          <button data-testid={`sat-count-${k}`} onClick={() => setOpen(open === k ? null : k)}
            className="w-full flex items-center justify-between font-mono-t text-[10px] text-slate-300 hover:text-cyan">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: SAT_COLORS[k] }} />{LABELS[k]}</span>
            <span className="font-bold" style={{ color: SAT_COLORS[k] }}>{v.counts[k]}</span>
          </button>
          {open === k && (
            <div className="pl-3.5 max-h-28 overflow-y-auto">
              {v[k].length === 0 && <div className="font-mono-t text-[9px] text-slate-600">None</div>}
              {v[k].map((a) => (
                <button key={a.id} data-testid={`sat-asset-${a.id}`} onClick={() => onSelect(a)}
                  className="block w-full text-left font-mono-t text-[9px] text-slate-400 hover:text-cyan truncate">
                  {a.asset_type} · {a.name}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Result({ r, shown, onToggle, onSelect, view, setView, photos }) {
  if (r.status !== "ok") {
    return <div data-testid="sat-result-message" className="font-mono-t text-[10px] text-slate-400">{r.message || r.status}</div>;
  }
  const v = r.validation;
  return (
    <div data-testid="sat-result" className="space-y-2">
      <div className="font-mono-t text-[10px] text-slate-400 leading-relaxed">
        After: {fmt(r.after.time)} ({r.after.platform}, orbit {r.after.orbit})
        {r.event !== "latest" && r.hours_after_event != null && <> · <span className="text-slate-200">{r.hours_after_event} h after storm</span></>}<br />
        Before: {fmt(r.before.time)} · same orbit
        {r.baseline && <><br />Noise check: {fmt(r.baseline.time)} → <span data-testid="sat-baseline-km2">{r.baseline_km2}</span> km² change with no storm (removed)</>}
      </div>
      <div className="flex items-baseline gap-2">
        <span data-testid="sat-flood-km2" className="font-heading text-xl font-bold text-sky-300">{r.flood_km2}</span>
        <span className="font-mono-t text-[10px] text-slate-400">km² new surface water of {r.analysed_land_km2} km² land imaged</span>
      </div>
      {v && (v.compared ? <Counts v={v} onSelect={onSelect} /> : (
        <div className="font-mono-t text-[10px] text-slate-300">
          Assets with radar-detected water: <span className="font-bold text-sky-300">{v.counts.radar_wet}</span>
        </div>
      ))}
      <PhotoStrip id={r.event} photos={photos} view={view} setView={setView} />
      <button data-testid="sat-toggle-map" onClick={onToggle}
        className="w-full flex items-center justify-center gap-1.5 border border-sky-400/50 text-sky-300 rounded-md py-1.5 font-mono-t text-[10px] uppercase tracking-wide hover:bg-sky-500/10 transition-colors">
        {shown ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {shown ? "Hide from map" : `Show ${view === "radar" ? "radar" : `${view} photo`} on map`}
      </button>
    </div>
  );
}

export default function SatellitePanel({ onShow, shownId, onSelect }) {
  const [events, setEvents] = useState([]);
  const [sel, setSel] = useState("beryl2024");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("radar");
  const [photos, setPhotos] = useState(null);
  const current = useRef(sel);

  const load = useCallback(async (id) => {
    try {
      const r = await getSat(id);
      if (current.current === id) setRes(r);
    } catch (e) { if (current.current === id) setRes(null); }
  }, []);
  const pick = (id) => {
    if (id === sel) return;
    if (shownId) onShow(null);
    current.current = id; setRes(null); setPhotos(null); setView("radar"); setSel(id);
  };
  const overlayFor = (v) => (v === "radar" ? res?.overlay
    : photos?.[v] ? { url: photos[v].url, bounds: photos.bounds } : null);
  const toggle = () => onShow(shownId === sel ? null : { id: sel, overlay: overlayFor(view), validation: res.validation });
  const changeView = (v) => {
    setView(v);
    if (shownId === sel) onShow({ id: sel, overlay: overlayFor(v), validation: res.validation });
  };

  useEffect(() => { getSatEvents().then((d) => setEvents(d.events)).catch(() => {}); }, []);
  useEffect(() => { load(sel); }, [sel, load]);
  useEffect(() => {
    if (res?.status !== "ok") return undefined;
    let stop = false;
    const id = sel;
    const poll = async () => {
      try {
        const p = await getPhotos(id);
        if (stop || current.current !== id) return;
        setPhotos(p);
        if (p.status === "running") setTimeout(poll, 5000);
      } catch (e) {
        if (stop || e?.response?.status !== 404) return;
        try { await startPhotos(id); setPhotos({ status: "running" }); setTimeout(poll, 5000); }
        catch (ex) { setPhotos({ status: "error", message: formatApiError(ex) }); }
      }
    };
    poll();
    return () => { stop = true; };
  }, [res?.status, sel]);
  useEffect(() => {
    if (res?.status !== "running") return undefined;
    const t = setInterval(() => load(sel), 5000);
    return () => clearInterval(t);
  }, [res?.status, sel, load]);

  const ev = events.find((e) => e.id === sel);
  const run = async () => {
    setBusy(true);
    try { await analyseSat(sel); await load(sel); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const running = res?.status === "running" || busy;

  return (
    <div data-testid="satellite-panel" className="glass rounded-lg p-3 space-y-2.5">
      <div className="flex items-center gap-2">
        <Satellite className="w-4 h-4 text-sky-300" />
        <h2 className="font-heading font-semibold uppercase tracking-wide text-sm text-slate-100">Radar Flood Check</h2>
        <span className="ml-auto font-mono-t text-[9px] text-slate-500">Sentinel-1 · Copernicus</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {events.map((e) => (
          <button key={e.id} data-testid={`sat-event-${e.id}`} onClick={() => pick(e.id)} disabled={!e.available}
            title={e.reason || ""}
            className={`px-2 py-1 rounded border font-mono-t text-[10px] uppercase transition-colors ${
              sel === e.id ? "bg-sky-500/15 border-sky-400/60 text-sky-300" : "border-border text-slate-400 hover:text-slate-200"
            } disabled:opacity-60 disabled:text-slate-500 disabled:cursor-not-allowed`}>
            {e.name}
          </button>
        ))}
      </div>
      {ev && !ev.available && <div className="font-mono-t text-[10px] text-slate-500">{ev.reason}</div>}
      {ev?.available && (!res || res.status === "error") && (
        <>
          {res?.status === "error" && <div data-testid="sat-error" className="font-mono-t text-[10px] text-risk-critical">{res.message}</div>}
          <button data-testid="sat-analyse-button" onClick={run} disabled={running}
            className="w-full flex items-center justify-center gap-1.5 bg-sky-400 text-slate-950 font-heading font-semibold uppercase tracking-wide text-xs py-2 rounded-md hover:bg-sky-300 transition-colors disabled:opacity-50">
            <Radar className="w-3.5 h-3.5" /> Analyse radar passes
          </button>
        </>
      )}
      {running && (
        <div data-testid="sat-running" className="flex items-center gap-2 font-mono-t text-[10px] text-slate-400">
          <Loader2 className="w-3 h-3 animate-spin" /> Finding passes and comparing before/after radar…
        </div>
      )}
      {res && res.status !== "running" && res.status !== "error" && (
        <Result r={res} shown={shownId === sel} onSelect={onSelect} onToggle={toggle}
          view={view} setView={changeView} photos={photos} />
      )}
    </div>
  );
}
